// Lo que hace cada herramienta de la edición en el visor —guard de saldo, llamada, entrega
// de la versión nueva— sin nada de su interfaz. Lo usan el panel de escritorio
// (`EdicionEnElVisor`) y la fila del teléfono (`EdicionEnElVisorMovil`), así las dos cobran
// y entregan igual (ADR-2026-10-28-editar-en-el-visor-del-celular). Antes vivía adentro del
// panel.

import { useEffect, useState } from 'react';
import { Crop, Expand, Layers, Maximize, Package, UserRound } from 'lucide-react';
import { guardarImagenComoElemento } from '../../pages/v2/elementosDeMarcaApi';
import { componerCapas, CREDITOS_EDITAR_CAPA, CREDITOS_SEPARAR_EN_CAPAS, editarCapa, guardarCapas, separarEnCapas, type CapaEnLienzo, type Separacion } from '../../lib/separarEnCapas';
import { buscarFila, type FilaEditada } from '../BarraDelVisor';
import { CREDITOS_EXPANSION, expandirImagen, type RatioExpansion } from '../../lib/expansionMagica';
import { GenerateAdError } from '../../lib/generateAdRequest';
import { MODO_UTILIZADO_EDICION, mensajeDeUpscaleFallido, postImageEdit, postImageUpscale, type ImageEditResponse } from '../../lib/imageEdit';
import { USE_ATLAS_BACKEND } from '../../config';
import { usePalabraDeModelo } from '../../lib/nombreDeModelo';
import { useRubroMarcaActiva } from '../../hooks/useRubroMarca';
import { firmarFotoDeModelo, type ModelV2 } from '../../pages/v2/modelsApi';
import { rubroDelPrompt } from '../../pages/v2/replicarFoto';
import { supabase } from '../../lib/supabase';
import type { Dimensiones } from '../../lib/upscaleLimits';
import { activarVersion, guardarRecorte } from '../../lib/versionesDelVisor';
import { setCachedSignedUrl } from '../../utils/imageCache';
import { consultarSaldo, tipoDeLimite, type TipoDeLimite } from '../../chat-atlas/lienzo/herramientas/saldo';
import { sonarListo } from '../../utils/sonarListo';

/** Sin herramienta elegida (`null`) es la edición de siempre: el prompt de abajo. */
export type HerramientaDeEdicion = 'recortar' | 'expandir' | 'mejorar' | 'producto' | 'modelo' | 'capas';

export const HERRAMIENTAS: { id: HerramientaDeEdicion; nombre: string; bajada: string; Icono: typeof Crop }[] = [
  { id: 'recortar', nombre: 'Recortar', bajada: 'Encuadrá y guardá una versión', Icono: Crop },
  { id: 'expandir', nombre: 'Expandir', bajada: 'Llevá la imagen a otro formato', Icono: Expand },
  { id: 'mejorar', nombre: 'Mejorar', bajada: 'Subí la resolución con IA', Icono: Maximize },
  // ADR-2026-10-06-cambiar-producto-y-modelo-en-el-visor. El nombre de "modelo" sigue al
  // rubro (Modelo / Avatar): se completa al dibujar la lista.
  { id: 'producto', nombre: 'Cambiar producto', bajada: 'Poné otro de tus productos', Icono: Package },
  { id: 'modelo', nombre: 'Cambiar modelo', bajada: 'Otra persona, la misma foto', Icono: UserRound },
  // ADR-2026-10-07-separar-en-capas-en-el-visor.
  { id: 'capas', nombre: 'Separar en capas', bajada: 'Mové, ordená u ocultá cada elemento', Icono: Layers },
];

export interface ImagenEnEdicion {
  id: string;
  fileName: string;
  /** La URL que muestra el visor. */
  srcVisible: string;
  /** La firmada del `full/` (recorte y medidas). */
  srcOriginal: string;
  /** Ya tiene una versión mejorada. */
  yaMejorada: boolean;
}

/** El id de la fila nueva de una edición, o el error de la respuesta. */
function idDeLaEdicion(data: ImageEditResponse): string {
  if (data.response === 'error_moderation') {
    throw new Error(data.error || 'La imagen no pasó el filtro de contenido. Probá con otra opción.');
  }
  const id = data.edited_image_id ?? data.imagen_id ?? data.id;
  if (data.response !== 'success' || !id) throw new Error(data.error || 'No pudimos editar la imagen. Probá de nuevo.');
  return String(id);
}

export interface OpcionesDeHerramientas {
  imagen: ImagenEnEdicion;
  companyId: string | null;
  /** Recorte, expansión o cambio: una fila nueva, que sale de `baseId`. */
  onVersionNueva: (fila: FilaEditada, baseId: string) => void;
  /** La mejora quedó en la misma imagen. */
  onMejorada: (upscaledPath: string, signedUrl: string, baseId: string) => void;
  /** Mientras corre algo (para apagar la imagen y mostrar el brillo), con su texto. */
  onTrabajo: (texto: string | null) => void;
  onEmpieza?: (trabajo: Promise<string | null>, info: { baseId: string; creditos: number }) => void;
  onLimite: (t: TipoDeLimite) => void;
  onError: (mensaje: string) => void;
  /** Terminó una herramienta: quien la dibuja vuelve a la lista
   *  (ADR-2026-10-06-al-terminar-el-panel-vuelve-a-la-lista). */
  onTerminar: () => void;
}

export function useHerramientasDeEdicion({
  imagen,
  companyId,
  onVersionNueva,
  onMejorada,
  onTrabajo,
  onEmpieza,
  onLimite,
  onError,
  onTerminar,
}: OpcionesDeHerramientas) {
  const [ocupado, setOcupado] = useState(false);
  const [medidas, setMedidas] = useState<Dimensiones | null>(null);
  const palabra = usePalabraDeModelo();
  const { rubro, marcaId } = useRubroMarcaActiva();

  // Las medidas del original (Expandir y Mejorar las necesitan). `crossOrigin` igual que el
  // recorte, así comparten la misma descarga del navegador.
  useEffect(() => {
    setMedidas(null);
    if (!imagen.srcOriginal) return;
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => img.naturalWidth > 0 && setMedidas({ width: img.naturalWidth, height: img.naturalHeight });
    img.src = imagen.srcOriginal;
  }, [imagen.srcOriginal]);

  /** `texto` null: sin el cartel ni el brillo sobre toda la imagen (editar una capa lo muestra sólo en ella). */
  const conGuard = async (costo: number, texto: string | null, trabajo: () => Promise<void>) => {
    if (ocupado || !companyId) return;
    if (costo > 0) {
      const saldo = await consultarSaldo(companyId);
      if (saldo.restantes !== null && saldo.restantes < costo) {
        onLimite(tipoDeLimite(saldo.plan));
        return;
      }
    }
    setOcupado(true);
    onTrabajo(texto);
    try {
      await trabajo();
    } catch (err) {
      if (err instanceof GenerateAdError && err.errorType === 'billing') {
        const saldo = await consultarSaldo(companyId);
        onLimite(tipoDeLimite(saldo.plan));
      } else {
        onError(err instanceof Error && err.message ? err.message : 'No pudimos procesar la imagen. Probá de nuevo.');
      }
    } finally {
      setOcupado(false);
      onTrabajo(null);
      try {
        window.dispatchEvent(new Event('atlas:credits-updated'));
      } catch {}
    }
  };

  /**
   * Una herramienta que crea imagen nueva: `crear` devuelve el id (o file_name) que dio el
   * backend; acá se encuentra la fila, se activa y se entrega. Todo eso es UNA promesa que
   * sale apenas arranca (`onEmpieza`), así la grilla de quien prestó el visor la sigue aunque
   * el visor se cierre (ADR-2026-10-06-las-ediciones-del-visor-aterrizan-en-la-grilla).
   */
  const entregar = async (baseId: string, creditos: number, crear: () => Promise<string>) => {
    if (!companyId) return;
    const trabajo = (async () => {
      const crudo = await crear();
      const fila = await buscarFila(companyId, crudo);
      if (!fila?.file_name) throw new Error('La versión se guardó, pero no pudimos cargarla. Mirá Creaciones.');
      await activarVersion(String(fila.id), companyId);
      return fila;
    })();
    onEmpieza?.(trabajo.then((f) => String(f.id)), { baseId, creditos });
    const fila = await trabajo;
    onVersionNueva(fila, baseId);
    onTerminar();
  };

  const recortar = (area: { x: number; y: number; width: number; height: number }, formato?: string) => {
    const baseId = imagen.id;
    void conGuard(0, 'Guardando el recorte…', async () => {
      await entregar(baseId, 0, () => guardarRecorte({ imagenId: baseId, area, formato }));
    });
  };

  const expandir = (ratio: RatioExpansion) => {
    const baseId = imagen.id;
    void conGuard(CREDITOS_EXPANSION, 'Expandiendo la imagen…', async () => {
      if (!companyId) return;
      await entregar(baseId, CREDITOS_EXPANSION, async () => {
        const r = await expandirImagen({ imagenId: baseId, aspectRatio: ratio, companyId, modoUtilizado: 'expansion_magica_visor' });
        return r.id;
      });
    });
  };

  const mejorar = (escala: 2 | 4) => {
    const baseId = imagen.id;
    void conGuard(escala === 2 ? 1 : 2, 'Mejorando la resolución…', async () => {
      const {
        data: { user },
      } = await supabase.auth.getUser();
      if (!user) throw new Error('No pudimos identificar tu cuenta. Recargá la página.');
      const data = await postImageUpscale({ user_id: user.id, image_id: baseId, scale: escala });
      if (data.success) {
        const path = data.upscaled_path || `${companyId}/upscaled/${imagen.fileName}`;
        const { data: firmada } = await supabase.storage.from('generadas').createSignedUrl(path, 3600);
        if (!firmada?.signedUrl) throw new Error('La imagen se mejoró, pero no pudimos cargarla.');
        setCachedSignedUrl(path, firmada.signedUrl);
        // Si mientras tanto se cambió de versión, igual se avisa: la mejora es de `baseId`.
        onMejorada(path, firmada.signedUrl, baseId);
        onTerminar();
      } else if (data.error === 'insufficient_credits') {
        onLimite(tipoDeLimite(data.plan));
      } else {
        throw new Error(mensajeDeUpscaleFallido(data));
      }
    });
  };

  // Separar en capas: 1 crédito; las capas quedan en el lienzo del modo, sin fila nueva.
  const separar = (instruccion: string | undefined, alTerminar: (s: Separacion) => void) => {
    const baseId = imagen.id;
    void conGuard(CREDITOS_SEPARAR_EN_CAPAS, 'Separando en capas…', async () => {
      alTerminar(await separarEnCapas({ imagenId: baseId, instruccion }));
      // Separar no crea una versión (no pasa por `onVersionNueva`), así que suena acá.
      sonarListo();
    });
  };

  // Editar UNA capa con Sunburst (fondo transparente), 1 crédito; queda en el lienzo, sin fila
  // (ADR-2026-10-07-editar-una-capa-con-sunburst).
  /** Guarda el PNG de una capa en Elementos de marca → "Elementos gráficos". Gratis
   *  (ADR-2026-10-20-una-capa-se-guarda-como-elemento-de-marca). */
  const guardarCapaComoElemento = async (capa: CapaEnLienzo) => {
    if (!companyId || !marcaId) throw new Error('Elegí una marca para guardar elementos.');
    return guardarImagenComoElemento({ companyId, brandId: marcaId, url: capa.src, titulo: capa.nombre });
  };

  const editarUnaCapa = (capa: CapaEnLienzo, instruccion: string, alTerminar: (nueva: CapaEnLienzo | null) => void) => {
    let entregada = false;
    void conGuard(CREDITOS_EDITAR_CAPA, null, async () => {
      if (!companyId) return;
      const nueva = await editarCapa({ capa, companyId, instruccion });
      entregada = true;
      alTerminar(nueva);
      sonarListo();
    }).finally(() => {
      // Sin saldo, error o sin empresa: la capa sale del estado "editando".
      if (!entregada) alTerminar(null);
    });
  };

  // Guardar las capas: la composición es lo que se ve, y queda como versión nueva (gratis).
  const guardarLasCapas = (datos: { capas: CapaEnLienzo[]; ancho: number; alto: number; formato?: string }) => {
    const baseId = imagen.id;
    void conGuard(0, 'Guardando la versión…', async () => {
      if (!companyId) return;
      await entregar(baseId, 0, async () =>
        guardarCapas({
          imagenId: baseId,
          companyId,
          png: await componerCapas(datos.capas, datos.ancho, datos.alto),
          formato: datos.formato,
        }),
      );
    });
  };

  // "Cambiar modelo" usa el modo `replicar`, que sólo existe en el backend nuevo; y se llama
  // como la app llama a las personas en este rubro (Modelo / Avatar).
  // "Separar en capas" también es sólo del backend nuevo (`/v1/images/layers` con Seedream).
  const herramientasVisibles = HERRAMIENTAS.filter((h) => (h.id !== 'modelo' && h.id !== 'capas') || USE_ATLAS_BACKEND).map((h) =>
    h.id === 'modelo' ? { ...h, nombre: `Cambiar ${palabra.singular}` } : h,
  );

  const cambiarProducto = (varianteId: string) => {
    const baseId = imagen.id;
    void conGuard(1, 'Cambiando el producto…', async () => {
      if (!companyId) return;
      await entregar(baseId, 1, async () => {
        const {
          data: { user },
        } = await supabase.auth.getUser();
        if (!user) throw new Error('No pudimos identificar tu cuenta. Recargá la página.');
        const data = await postImageEdit(
          {
            user_id: user.id,
            imagen_id: baseId,
            variante_id: varianteId,
            mode: 'cambio_producto',
            modo_utilizado: MODO_UTILIZADO_EDICION.cambio_producto ?? 'editar_imagen_producto',
          },
          { companyId },
        );
        return idDeLaEdicion(data);
      });
    });
  };

  // Cambiar la persona: el modo `replicar` del backend sobre la creación, con SÓLO la foto
  // del modelo (sin producto ni instrucción): reemplaza a la persona en la misma pose, luz
  // y escena, y la fila nueva hereda productos y formato (sin `aspect_ratio`).
  const cambiarModelo = (modelo: ModelV2) => {
    const baseId = imagen.id;
    void conGuard(1, `Cambiando ${palabra.singular === 'modelo' ? 'la modelo' : 'el avatar'}…`, async () => {
      if (!companyId) return;
      await entregar(baseId, 1, async () => {
        // La foto se firma recién ahora: la del listado puede estar vencida.
        const url = await firmarFotoDeModelo(modelo);
        if (!url) throw new Error(`No pudimos leer la foto de ${modelo.nombre}.`);
        const {
          data: { user },
        } = await supabase.auth.getUser();
        if (!user) throw new Error('No pudimos identificar tu cuenta. Recargá la página.');
        const data = await postImageEdit(
          {
            user_id: user.id,
            mode: 'replicar',
            imagen_id: baseId,
            modelo_url: url,
            rubro: rubroDelPrompt(rubro),
            modo_utilizado: 'cambiar_modelo_visor',
          },
          { companyId },
        );
        return idDeLaEdicion(data);
      });
    });
  };


  return {
    ocupado,
    medidas,
    herramientasVisibles,
    recortar,
    expandir,
    mejorar,
    separar,
    editarUnaCapa,
    guardarCapaComoElemento,
    guardarLasCapas,
    cambiarProducto,
    cambiarModelo,
  };
}
